import { useState } from "react";
import ImageHeading from "../../components/ui/ImageHeading";
import headerImage from "../../assets/header/header.jpeg";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import OverviewCSE from "../../components/Departments/CSE/OverviewCSE";
import FacultyCSE from "../../components/Departments/CSE/FacultyCSE";
import CurriculumCSE from "../../components/Departments/CSE/CurriculumCSE";
import { useParams } from "react-router-dom";
import useAxiosPublic from "../../hooks/useAxiosPublic";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../../components/Layout/LoadingPage";

const tabData = {
  overview: {
    name: "Overview",
    component: OverviewCSE,
    dataKey: "overview",
  },
  faculty: {
    name: "Faculty",
    component: FacultyCSE,
    dataKey: "faculty",
  },
  curriculum: {
    name: "Curriculum",
    component: CurriculumCSE,
    dataKey: "curriculum",
  },
};

const DepartmentCSE = () => {
  const [active, setActive] = useState("overview");
  const { slug } = useParams();
  const axiosPublic = useAxiosPublic();

  const {
    data: departmentApiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["program", slug],
    queryFn: async () => {
      const res = await axiosPublic.get(`/academics/department/${slug}`);
      return res?.data?.data;
    },
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">Error loading data</div>
    );
  }

  // pick the correct tab component
  const ActiveComponent = tabData[active]?.component;
  const activeData = departmentApiData?.[tabData[active]?.dataKey];

  return (
    <div className="min-h-screen">
      <ImageHeading
        heading={departmentApiData?.department?.name}
        imageUrl={headerImage}
      />

      <div className="container px-4 mx-auto my-12 md:px-8">
        <h2 className="flex flex-col space-y-3 font-bold font-domine">
          <span className="text-base lg:text-xl font-display">
            {departmentApiData?.department?.name}
          </span>
          <span className="text-xl md:text-2xls lg:text-4xl">
            {departmentApiData?.department?.slug}
          </span>
        </h2>
        <p className="mt-4 font-display">
          {departmentApiData?.department?.description}
        </p>

        {/* Tab Buttons */}
        <div
          className={cn(
            "mb-8 flex flex-row items-center justify-left overflow-scroll [perspective:1000px] relative sm:overflow-visible h-full no-visible-scrollbar max-w-full w-full mt-8"
          )}
        >
          <div className="px-8 py-4 bg-white rounded-md shadow-xl lg:rounded-full w-fit">
            {Object.entries(tabData).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                className={cn("relative px-4 py-2 rounded-full")}
                style={{
                  transformStyle: "preserve-3d",
                }}
              >
                {active === key && (
                  <motion.div
                    layoutId="clickedbutton"
                    transition={{ type: "spring", bounce: 0.3, duration: 0.6 }}
                    className={cn(
                      "absolute inset-0 bg-primary text-white rounded-full"
                    )}
                  />
                )}
                <span
                  className={`relative block ${
                    active === key ? "text-white" : "text-black"
                  }`}
                >
                  {value.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Section */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active}
            className="overflow-hidden shadow-xl bg-white/80 backdrop-blur-sm rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {ActiveComponent && (
              <ActiveComponent
                departmentName={departmentApiData?.department?.name}
                data={activeData}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DepartmentCSE;
